import { useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import { useGetAssistant } from '@/hooks';
import { AppMessageSeverities } from '@/types';
import {
  Button,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  PageHeaderLayout,
  Skeleton,
  TextField,
  Typography,
} from '@/ui';

import type { Assistant as AssistantType } from '@/types';

import {
  useDeleteAssistantFunctionMutation,
  useGetAvailableModelsQuery,
  useUpdateAssistantMutation,
} from '@/store/api/assistantApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';
import { captureException } from '@/utils/errorMonitoring';

const Assistant = () => {
  const { assistantId = '' } = useParams();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const {
    data = {} as AssistantType,
    isLoading,
    refetch,
  } = useGetAssistant(assistantId);
  const { data: modelsData, isLoading: modelsLoading } =
    useGetAvailableModelsQuery();
  const [deleteAssistantFunction] = useDeleteAssistantFunctionMutation();
  const [updateAssistant] = useUpdateAssistantMutation();

  const renderModelOptions = () => {
    if (modelsLoading) {
      return <MenuItem disabled>Loading models...</MenuItem>;
    }

    if (!modelsData?.models.length) {
      return <MenuItem disabled>No models available</MenuItem>;
    }

    return modelsData.models.map((modelOption) => (
      <MenuItem key={modelOption.key} value={modelOption.value}>
        {modelOption.label}
      </MenuItem>
    ));
  };

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      assistantName: data.name || '',
      model: data.model || '',
      prompt: data.prompt || '',
    },
    values: {
      assistantName: data.name || '',
      model: data.model || '',
      prompt: data.prompt || '',
    },
  });

  const { field: assistantName, fieldState: assistantNameState } =
    useController({
      control,
      name: 'assistantName',
      rules: { required: true },
    });
  const { field: model, fieldState: modelState } = useController({
    control,
    name: 'model',
    rules: { required: true },
  });
  const { field: prompt, fieldState: promptState } = useController({
    control,
    name: 'prompt',
    rules: { required: true },
  });

  const navigate = useNavigate();
  const onBack = () => navigate('/coach/manage/assistants');
  const onFunctionClick = (id: string) => () => {
    navigate(`/coach/manage/assistants/${assistantId}/function/${id}`);
  };
  const onFunctionAddClick = () => {
    navigate(`/coach/manage/assistants/${assistantId}/function`);
  };
  const onFunctionDeleteClick = (id: string) => async () => {
    try {
      await deleteAssistantFunction(id).unwrap();
      refetch();
    } catch (e) {
      captureException(e);
      dispatch(
        setAppMessage({
          message: 'Error deleting function',
          open: true,
          severity: AppMessageSeverities.Error,
        }),
      );
    }
  };
  const onSave = () => {
    handleSubmit(async (formData) => {
      setLoading(true);
      try {
        await updateAssistant({
          id: assistantId,
          model: formData.model,
          name: formData.assistantName,
          prompt: formData.prompt,
        }).unwrap();
        refetch();
        dispatch(
          setAppMessage({
            message: 'Changes saved',
            open: true,
            severity: AppMessageSeverities.Success,
          }),
        );
      } catch (e) {
        captureException(e);
        dispatch(
          setAppMessage({
            message: 'Error updating assistant',
            open: true,
            severity: AppMessageSeverities.Error,
          }),
        );
      } finally {
        setLoading(false);
      }
    })();
  };

  if (isLoading) {
    return (
      <div className="max-w-screen-sm p-6">
        <PageHeaderLayout title="Assistant" />
        <div className="flex flex-col gap-2 ">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-sm p-6">
      <div className="flex flex-col gap-4">
        <Typography variant="h4">Assistant</Typography>

        <TextField
          error={assistantNameState.invalid}
          helperText="Required"
          label="Name"
          variant="outlined"
          {...assistantName}
        />
        <TextField
          error={promptState.invalid}
          helperText="Required"
          label="Prompt"
          maxRows={5}
          variant="outlined"
          multiline
          {...prompt}
        />
        <TextField
          error={modelState.invalid}
          helperText="Required"
          label="Model"
          variant="outlined"
          select
          {...model}
        >
          {renderModelOptions()}
        </TextField>
        <div className="flex gap-2">
          <Button
            className="self-baseline"
            color="primary"
            disabled={loading}
            variant="outlined"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            className="self-baseline"
            color="primary"
            disabled={!isValid}
            loading={loading}
            variant="contained"
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4">
        <Typography variant="h6">Functions</Typography>

        <List className="p-0">
          {(data.functions ?? []).map((fun) => (
            <ListItem
              key={fun.id}
              secondaryAction={
                <IconButton onClick={onFunctionDeleteClick(fun.id)}>
                  <Icon>trash</Icon>
                </IconButton>
              }
              disableGutters
            >
              <ListItemButton onClick={onFunctionClick(fun.id)}>
                <ListItemText>{fun.name}</ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <div className="flex gap-2">
          <Button
            className="self-baseline"
            color="primary"
            variant="contained"
            onClick={onFunctionAddClick}
          >
            Add function
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
